import type { StaticWhitespaceCardProps } from 'components/organism/WhitespaceAnalysis/StaticWhitespaceCard';
import { FilterContextData } from 'contexts/FilterContext';
import type { WhitespaceGridRow, WhitespaceGridCell } from 'hooks/useWhitespaceAnalysisGridData';
import { useCallback, useContext, useEffect, useState } from 'react';
import { downloadExcel } from 'utils/ppaHelpers';
import { downloadCsv, formatWord, sendNotification } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_WHITESPACE_EXPORT_CLICKED,
    PPA_WHITESPACE_EXPORT_CONFIRMED,
    PPA_WHITESPACE_EXPORT_FAILED,
    PPA_WHITESPACE_EXPORT_MODAL_VIEWED,
    PPA_WHITESPACE_EXPORT_SCOPE_SELECTED,
    PPA_WHITESPACE_EXPORT_SUCCEEDED,
} from 'utils/mixpanelEventNames';
import {
    buildWhitespaceExportRows,
    buildWhitespaceXlsxDataRows,
    type WhitespaceExportFormat,
    type WhitespaceExportScope,
} from './whitespaceExportConfig';
import { formatWhitespaceExportRowsForCsv } from './whitespaceExportValueFormatters';
import type { computeWhitespaceGridMetrics } from './whitespaceMixpanel.helpers';

type GridMetrics = ReturnType<typeof computeWhitespaceGridMetrics>;

export type WhitespaceExportOption = {
    value: string;
    label: string;
    disabled?: boolean;
};

export const WHITESPACE_EXPORT_SCOPE_OPTIONS: WhitespaceExportOption[] = [
    { value: 'current_view', label: 'Current View' },
];

export const WHITESPACE_EXPORT_FORMAT_OPTIONS: WhitespaceExportOption[] = [
    { value: 'xlsx', label: 'download as .xlsx' },
    { value: 'csv', label: 'download as .csv' },
];

type CellPaginationData = {
    items: StaticWhitespaceCardProps['items'];
    moreCount: number;
};

type UseWhitespaceExportParams = {
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
    comparisonMode: string;
    gridMetrics: GridMetrics;
    columnHeaders: string[];
    gridRows: WhitespaceGridRow[];
    selectedYAxisKpiId: string;
    getCellPaginationData: (params: {
        bucketLabel: string;
        columnHeader: string;
        defaultItems: StaticWhitespaceCardProps['items'];
        defaultMoreCount: number;
    }) => CellPaginationData;
};

const prepareWhitespaceExportGridRows = (
    gridRows: WhitespaceGridRow[],
    columnHeaders: string[],
    getCellPaginationData: UseWhitespaceExportParams['getCellPaginationData'],
): WhitespaceGridRow[] =>
    gridRows.map((row) => {
        const cells = columnHeaders.reduce<Record<string, WhitespaceGridCell | null>>((acc, columnHeader) => {
            const cell = row.cells[columnHeader];
            if (!cell) {
                acc[columnHeader] = null;
                return acc;
            }
            const paginationData = getCellPaginationData({
                bucketLabel: row.bucketLabel,
                columnHeader,
                defaultItems: cell.items ?? [],
                defaultMoreCount: cell.moreCount ?? 0,
            });
            acc[columnHeader] = {
                ...cell,
                items: paginationData.items,
                moreCount: paginationData.moreCount,
            };
            return acc;
        }, {});
        return { ...row, cells };
    });

const buildWhitespaceExportFileName = () => {
    const now = new Date();
    const date = now.toISOString().slice(0, 10);
    const time = now.toTimeString().slice(0, 8).replace(/:/g, '-');
    return `whitespace_analysis_${date}_${time}`;
};

const formatWhitespaceExportDateTime = (dateTime: Date) => {
    const month = dateTime.getMonth() + 1;
    const day = dateTime.getDate();
    const year = dateTime.getFullYear();
    const hours24 = String(dateTime.getHours()).padStart(2, '0');
    const minutes = String(dateTime.getMinutes()).padStart(2, '0');

    return `${month}/${day}/${year} ${hours24}:${minutes}`;
};

const buildWhitespaceExportMetadataRows = ({
    allCheckedAttribute,
    dimensionOrder,
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
}: {
    allCheckedAttribute: Record<string, unknown> | undefined;
    dimensionOrder: string[];
    xAxis: string;
    yAxis: string;
    crossSection: string;
    showWhitespaces: boolean;
}): Array<Array<string | number>> => {
    const checkedEntries = Object.entries(allCheckedAttribute || {});
    const orderedEntries =
        dimensionOrder.length > 0
            ? [
                  ...dimensionOrder
                      .map((dim) => checkedEntries.find(([key]) => key === dim))
                      .filter((entry): entry is [string, unknown] => entry !== undefined),
                  ...checkedEntries.filter(([key]) => !dimensionOrder.includes(key)),
              ]
            : checkedEntries;

    const appliedFilterGroups = orderedEntries
        .map(([attributeType, selectedValues]) => ({
            label: formatWord(attributeType),
            items: Object.keys((selectedValues as Record<string, unknown>) || {}),
        }))
        .filter((group) => group.items.length > 0);
    const globalFiltersValue = appliedFilterGroups
        .map((group) => `${group.label}: ${group.items.join(', ')}`)
        .join(' | ');
    const exportDate = formatWhitespaceExportDateTime(new Date());
    const exportedBy = localStorage.getItem('email') ?? '-';

    return [
        ['Global filters', globalFiltersValue || 'No global filters selected'],
        ['X-Axis', xAxis || '-'],
        ['Y-Axis', yAxis || '-'],
        ['Cross Section', crossSection || '-'],
        ['Show Whitespaces toggle', showWhitespaces ? 'ON' : 'OFF'],
        [],
        ['Export Date', exportDate],
        ['Exported By', exportedBy],
        [],
    ];
};

const writeWhitespaceExportFile = async (
    format: WhitespaceExportFormat,
    fileName: string,
    exportRows: Array<Array<string | number>>,
    options?: {
        columnFormats?: Array<string | null>;
        dataStartRow?: number;
    },
): Promise<void> => {
    if (format === 'xlsx') {
        await downloadExcel(fileName, exportRows, {
            moduleKey: 'whitespace_analysis',
            columnFormats: options?.columnFormats,
            dataStartRow: options?.dataStartRow,
        });
        return;
    }

    const csvEscape = (value: string | number) => {
        const stringValue = String(value ?? '');
        if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
    };
    const bom = '\uFEFF';
    const csvContent = exportRows.map((row) => row.map(csvEscape).join(',')).join('\n');
    downloadCsv([bom + csvContent], `${fileName}.csv`);
};

const useWhitespaceExport = ({
    xAxis,
    yAxis,
    crossSection,
    showWhitespaces,
    comparisonMode,
    gridMetrics,
    columnHeaders,
    gridRows,
    selectedYAxisKpiId,
    getCellPaginationData,
}: UseWhitespaceExportParams) => {
    const { allCheckedAttribute, allDimAndAttributeType } = useContext(FilterContextData);
    const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleOpenDownloadModal = useCallback(() => {
        trackEventMixpanel(PPA_WHITESPACE_EXPORT_CLICKED, {
            x_axis: xAxis,
            y_axis: yAxis,
            cross_section: crossSection,
            show_whitespaces: showWhitespaces,
            comparison_mode: comparisonMode,
            total_cell_count: gridMetrics.total_cell_count,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        });
        setIsDownloadModalOpen(true);
    }, [
        comparisonMode,
        crossSection,
        gridMetrics.opportunity_cell_count,
        gridMetrics.total_cell_count,
        showWhitespaces,
        xAxis,
        yAxis,
    ]);

    const handleCloseDownloadModal = useCallback(() => setIsDownloadModalOpen(false), []);

    useEffect(() => {
        if (!isDownloadModalOpen) return;
        trackEventMixpanel(PPA_WHITESPACE_EXPORT_MODAL_VIEWED, {
            available_export_scopes: WHITESPACE_EXPORT_SCOPE_OPTIONS.map((option) => option.value),
            default_export_scope: WHITESPACE_EXPORT_SCOPE_OPTIONS[0]?.value ?? 'current_view',
            supported_formats: WHITESPACE_EXPORT_FORMAT_OPTIONS.map((option) => option.value),
            total_cell_count: gridMetrics.total_cell_count,
            opportunity_cell_count: gridMetrics.opportunity_cell_count,
        });
    }, [gridMetrics.opportunity_cell_count, gridMetrics.total_cell_count, isDownloadModalOpen]);

    const handleExportScopeChange = useCallback(
        (selectedScope: string, previousScope: string | null) => {
            const exportGridRows = gridRows.map((row) => ({ ...row, cells: { ...row.cells } }));
            const { rows } = buildWhitespaceExportRows({
                gridRows: exportGridRows,
                columnHeaders,
                xAxisLabel: xAxis,
                yAxisLabel: yAxis,
                crossSection,
                scope: selectedScope as WhitespaceExportScope,
                selectedYAxisKpiId,
            });

            trackEventMixpanel(PPA_WHITESPACE_EXPORT_SCOPE_SELECTED, {
                previous_export_scope:
                    previousScope ?? WHITESPACE_EXPORT_SCOPE_OPTIONS[0]?.value ?? 'current_view',
                selected_export_scope: selectedScope,
                estimated_cell_count: gridMetrics.visible_cell_count,
                estimated_product_row_count: rows.length,
            });
        },
        [columnHeaders, crossSection, gridMetrics.visible_cell_count, gridRows, selectedYAxisKpiId, xAxis, yAxis],
    );

    const handleDownloadExport = useCallback(
        async ({ scope, format }: { scope: string; format: string }) => {
            const resolvedScope = scope as WhitespaceExportScope;
            const resolvedFormat = format as WhitespaceExportFormat;
            const exportStartedAt = performance.now();
            setIsExporting(true);

            trackEventMixpanel(PPA_WHITESPACE_EXPORT_CONFIRMED, {
                export_scope: resolvedScope,
                export_format: resolvedFormat,
                x_axis: xAxis,
                y_axis: yAxis,
                cross_section: crossSection,
                show_whitespaces: showWhitespaces,
                comparison_mode: comparisonMode,
            });

            const exportGridRows = prepareWhitespaceExportGridRows(gridRows, columnHeaders, getCellPaginationData);
            const exportResult = buildWhitespaceExportRows({
                gridRows: exportGridRows,
                columnHeaders,
                xAxisLabel: xAxis,
                yAxisLabel: yAxis,
                crossSection,
                scope: resolvedScope,
                selectedYAxisKpiId,
            });
            const { headers, rows, displayRows, columnFormats } = exportResult;

            if (rows.length === 0) {
                setIsExporting(false);
                sendNotification('info', 'No data available to export for the selected option.');
                return;
            }

            const fileName = buildWhitespaceExportFileName();
            const dimensionOrder: string[] =
                (allDimAndAttributeType?.dimentions ?? []).map(
                    (dim: { dimensionReference?: string }) => dim.dimensionReference ?? '',
                ).filter(Boolean);
            const exportMetadataRows = buildWhitespaceExportMetadataRows({
                allCheckedAttribute,
                dimensionOrder,
                xAxis,
                yAxis,
                crossSection,
                showWhitespaces,
            });
            const headerRowIndex = exportMetadataRows.length;
            const xlsxDataRows = buildWhitespaceXlsxDataRows(rows, displayRows);
            const exportRows = [...exportMetadataRows, headers, ...xlsxDataRows];
            const csvRows = formatWhitespaceExportRowsForCsv(
                [...exportMetadataRows, headers, ...displayRows],
                columnFormats,
                headerRowIndex,
            );

            try {
                if (resolvedFormat === 'xlsx') {
                    await writeWhitespaceExportFile(resolvedFormat, fileName, exportRows, {
                        columnFormats,
                        dataStartRow: headerRowIndex + 1,
                    });
                } else {
                    await writeWhitespaceExportFile(resolvedFormat, fileName, csvRows);
                }

                trackEventMixpanel(PPA_WHITESPACE_EXPORT_SUCCEEDED, {
                    export_scope: resolvedScope,
                    export_format: resolvedFormat,
                    file_name: resolvedFormat === 'xlsx' ? `${fileName}.xlsx` : `${fileName}.csv`,
                    exported_cell_count: gridMetrics.visible_cell_count,
                    exported_product_row_count: rows.length,
                    sheet_count: 1,
                    duration_ms: Math.round(performance.now() - exportStartedAt),
                });

                if (resolvedScope === 'all_filtered_cells') {
                    sendNotification(
                        'info',
                        'Export includes currently loaded grid data. Scroll/load more rows and columns to include additional cells.',
                    );
                } else {
                    sendNotification('success', 'Export completed.');
                }
                setIsDownloadModalOpen(false);
            } catch (error) {
                trackEventMixpanel(PPA_WHITESPACE_EXPORT_FAILED, {
                    export_scope: resolvedScope,
                    export_format: resolvedFormat,
                    error_type: error instanceof Error ? error.name : 'export_error',
                    error_message: error instanceof Error ? error.message : String(error),
                    failed_request_id: '-',
                    retry_available: true,
                });
                sendNotification('error', 'Failed to export whitespace data. Please try again.');
            } finally {
                setIsExporting(false);
            }
        },
        [
            allCheckedAttribute,
            allDimAndAttributeType,
            columnHeaders,
            comparisonMode,
            crossSection,
            getCellPaginationData,
            gridMetrics.visible_cell_count,
            gridRows,
            selectedYAxisKpiId,
            showWhitespaces,
            xAxis,
            yAxis,
        ],
    );

    return {
        isDownloadModalOpen,
        isExporting,
        handleOpenDownloadModal,
        handleCloseDownloadModal,
        handleExportScopeChange,
        handleDownloadExport,
    };
};

export default useWhitespaceExport;
